import axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTrash, FaCheckDouble, FaBell, FaShoppingBag, FaTimesCircle, FaCheckCircle, FaChevronRight, FaRegTrashAlt } from "react-icons/fa";
import LoadingScreen from '../../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';
const token = localStorage.getItem('authToken');

function AllNotificationsPage() {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const fetchNotifications = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/notifications`);
            setNotifications(response.data || []);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    const markAllRead = async () => {
        try {
            await axios.patch(`${API_BASE_URL}/notifications/mark-read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchNotifications();
        } catch (error) {
            handleApiError(error);
        }
    };

    const deleteNotification = async (e, id) => {
        e.stopPropagation(); // Prevent card click navigation
        try {
            await axios.delete(`${API_BASE_URL}/notifications/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchNotifications();
        } catch (error) {
            handleApiError(error);
        }
    };

    const clearAllNotifications = async () => {
        if (!window.confirm("Are you sure you want to clear all notification history?")) return;
        try {
            await axios.delete(`${API_BASE_URL}/clear-all-notifications`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchNotifications();
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleNotificationClick = async (notif) => {
        if (!notif.read) {
            try {
                await axios.patch(`${API_BASE_URL}/notifications/mark-read`, { ids: [notif._id] }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (e) {
                console.error("Error marking single notification read:", e);
            }
        }
        if (notif.orderId?._id || notif.orderId) {
            const orderId = notif.orderId?._id || notif.orderId;
            navigate(`/admin/orders-details/${orderId}`);
        } else {
            fetchNotifications();
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, []);

    // Grouping helper
    const getGroupedNotifications = () => {
        const groups = { Today: [], Yesterday: [], Earlier: [] };
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);

        notifications.forEach(notif => {
            const notifDate = new Date(notif.createdAt);
            if (notifDate.toDateString() === today.toDateString()) {
                groups.Today.push(notif);
            } else if (notifDate.toDateString() === yesterday.toDateString()) {
                groups.Yesterday.push(notif);
            } else {
                groups.Earlier.push(notif);
            }
        });
        return groups;
    };

    const formatTime = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) + ', ' + d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    };

    const getIcon = (type) => {
        switch (type) {
            case 'new':
                return <FaShoppingBag className="text-primary fs-5" />;
            case 'cancelled':
                return <FaTimesCircle className="text-danger fs-5" />;
            default:
                return <FaCheckCircle className="text-success fs-5" />;
        }
    };

    const getIconBg = (type) => {
        switch (type) {
            case 'new':
                return 'bg-primary-subtle border border-primary-subtle';
            case 'cancelled':
                return 'bg-danger-subtle border border-danger-subtle';
            default:
                return 'bg-success-subtle border border-success-subtle';
        }
    };

    const grouped = getGroupedNotifications();
    const hasNotifications = notifications.length > 0;
    const hasUnread = notifications.some(n => !n.read);

    if (loading) {
        return <LoadingScreen />;
    }

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                {/* Header */}
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div className="section-title">
                                <h4 className="mt-1">NOTIFICATION HISTORY</h4>
                                <p className="text-muted p-0 m-0">View and manage all system activity logs</p>
                            </div>
                            {hasNotifications && (
                                <div className="d-flex gap-2">
                                    {hasUnread && (
                                        <button 
                                            type="button" 
                                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 px-3 py-2"
                                            onClick={markAllRead}
                                            style={{ borderRadius: '0', fontSize: '0.82rem', fontWeight: '600' }}
                                        >
                                            <FaCheckDouble size={11} />
                                            Mark all read
                                        </button>
                                    )}
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 px-3 py-2"
                                        onClick={clearAllNotifications}
                                        style={{ borderRadius: '0', fontSize: '0.82rem', fontWeight: '600' }}
                                    >
                                        <FaRegTrashAlt size={11} />
                                        Clear History
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Notifications History List */}
                <div className="row mt-4">
                    <div className="col-lg-8 col-md-10 col-12 mx-auto">
                        {hasNotifications ? (
                            Object.keys(grouped).map(groupName => {
                                const list = grouped[groupName];
                                if (list.length === 0) return null;

                                return (
                                    <div key={groupName} className="mb-4">
                                        <h6 className="text-muted fw-bold text-uppercase mb-3" style={{ fontSize: '0.75rem', letterSpacing: '1px' }}>
                                            {groupName}
                                        </h6>
                                        <div className="card border-0 shadow-sm" style={{ borderRadius: '0' }}>
                                            <div className="list-group list-group-flush">
                                                {list.map((notif) => (
                                                    <div 
                                                        key={notif._id} 
                                                        className={`list-group-item list-group-item-action d-flex align-items-start gap-3 p-3 border-0 border-bottom ${!notif.read ? 'bg-light-subtle' : ''}`}
                                                        onClick={() => handleNotificationClick(notif)}
                                                        style={{ 
                                                            cursor: 'pointer',
                                                            borderLeft: !notif.read ? '3px solid #3b82f6' : '3px solid transparent',
                                                            transition: 'all 0.2s ease',
                                                            borderBottomColor: '#cbd5e1'
                                                        }}
                                                    >
                                                        {/* Status Icon */}
                                                        <div className={`p-2 d-flex justify-content-center align-items-center rounded ${getIconBg(notif.type)}`} style={{ width: '40px', height: '40px' }}>
                                                            {getIcon(notif.type)}
                                                        </div>

                                                        {/* Details */}
                                                        <div className="flex-grow-1">
                                                            <div className="d-flex justify-content-between align-items-start gap-2">
                                                                <h6 className={`mb-1 ${!notif.read ? 'fw-bold text-dark' : 'fw-semibold text-secondary'}`} style={{ fontSize: '0.92rem' }}>
                                                                    {notif.title}
                                                                </h6>
                                                                <small className="text-muted text-nowrap" style={{ fontSize: '0.75rem' }}>
                                                                    {formatTime(notif.createdAt)}
                                                                </small>
                                                            </div>
                                                            <p className="mb-0 text-muted" style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                                                                {notif.message}
                                                            </p>
                                                        </div>

                                                        {/* Action Buttons */}
                                                        <div className="d-flex align-items-center gap-2">
                                                            <button 
                                                                type="button" 
                                                                className="btn btn-link text-muted p-1 hover-danger"
                                                                onClick={(e) => deleteNotification(e, notif._id)}
                                                                title="Delete log"
                                                                style={{ border: 'none', background: 'transparent' }}
                                                            >
                                                                <FaTrash size={12} />
                                                            </button>
                                                            <FaChevronRight className="text-muted-light" size={11} />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="card order-table-card">
                                <div className="card-body text-center py-5">
                                    <div className="mb-3 d-inline-flex p-3 rounded-circle bg-light">
                                        <FaBell className="text-muted fs-2" />
                                    </div>
                                    <h5 className="text-muted fw-bold">No Notifications Yet</h5>
                                    <p className="text-muted mb-0">Activity logs regarding new orders and changes will appear here.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style jsx>{`
                .hover-danger:hover {
                    color: #ed3237 !important;
                }
                .bg-light-subtle {
                    background-color: #f0f9ff !important;
                }
                .text-muted-light {
                    color: #cbd5e1;
                }
            `}</style>
        </Fragment>
    );
}

export default AllNotificationsPage;
